import { Lead, GuideSection, H3, P, UL, OL, LI, GuideLink } from './guide-ui';

/**
 * Guida in italiano (locale: 'it', logpulse.it): NIS2 e il decreto di
 * recepimento italiano. Volutamente un pezzo locale originale (D.Lgs.
 * 138/2024, ACN, registrazione, scadenza del 18 aprile 2026, sanzioni),
 * non una traduzione della guida NIS2 inglese: quella tratta la direttiva
 * in sé e resta raggiungibile come approfondimento tramite i related link.
 */
export function Nis2ItaliaGuideBody() {
  return (
    <>
      <Lead>
        NIS2 è la direttiva europea sulla cybersicurezza, recepita in Italia
        con il Decreto Legislativo 138/2024, in vigore dal 18 ottobre 2024.
        La fase di preparazione è finita: le entità si sono registrate presso
        l&rsquo;ACN, gli obblighi di base sono scaduti il 18 aprile 2026 e la
        vigilanza attiva è iniziata. Questa guida spiega chi rientra nel
        perimetro, cosa richiede il decreto e cosa significa in concreto per
        logging e monitoraggio.
      </Lead>

      <GuideSection id="cos-e-nis2" title="Dalla direttiva NIS2 al D.Lgs. 138/2024">
        <P>
          NIS2 (Direttiva (UE) 2022/2555) è l&rsquo;evoluzione della prima
          direttiva NIS e doveva essere recepita negli ordinamenti nazionali
          entro il 17 ottobre 2024. L&rsquo;Italia lo ha fatto nei tempi
          previsti con il{' '}
          <strong>Decreto Legislativo 4 settembre 2024, n. 138</strong>,
          pubblicato in Gazzetta Ufficiale il 1° ottobre 2024 e{' '}
          <strong>in vigore dal 18 ottobre 2024</strong>.
        </P>
        <P>
          A differenza di altri Paesi europei, in Italia la legge non è
          all&rsquo;orizzonte: c&rsquo;è già, e l&rsquo;attuazione segue un
          calendario preciso. Le entità essenziali e importanti si sono
          registrate presso l&rsquo;ACN nel 2025, gli obblighi organizzativi
          di base sono scaduti il <strong>18 aprile 2026</strong>, e nel 2026
          il baricentro si sposta sulla vigilanza attiva: ispezioni, audit e
          verifiche.
        </P>
        <P>
          La domanda quindi non è più se prepararsi, ma se la tua
          organizzazione è in grado di dimostrare oggi, davanti a
          un&rsquo;ispezione, che logging, rilevamento e processo di notifica
          funzionano davvero.
        </P>
      </GuideSection>

      <GuideSection id="sono-coinvolto" title="La mia organizzazione rientra in NIS2?">
        <P>
          Il decreto distingue due categorie, con vigilanza e sanzioni
          diverse:
        </P>
        <UL>
          <LI>
            <strong>Soggetti essenziali</strong>: grandi organizzazioni in
            settori come energia, trasporti, settore bancario, infrastrutture
            dei mercati finanziari, sanità, acqua potabile e acque reflue,
            infrastrutture digitali, pubblica amministrazione e spazio.
          </LI>
          <LI>
            <strong>Soggetti importanti</strong>: medie e grandi
            organizzazioni tra l&rsquo;altro in servizi postali e di
            corriere, gestione dei rifiuti, chimica, alimentare,
            manifatturiero (inclusi dispositivi medici ed elettronica),
            fornitori di servizi digitali e ricerca.
          </LI>
        </UL>
        <P>
          La soglia dimensionale di base è di circa 50 dipendenti o 10
          milioni di euro di fatturato annuo. Organizzazioni più piccole
          possono comunque rientrare nel perimetro, ad esempio come unico
          fornitore di un servizio essenziale o come parte di un gruppo più
          grande.
        </P>
        <P>
          Vale inoltre un <strong>obbligo di registrazione</strong>: le
          entità essenziali e importanti dovevano registrarsi nel 2025 sul
          portale dell&rsquo;ACN. Le organizzazioni che entrano nel perimetro
          in un momento successivo si registrano nella finestra annuale
          prevista dall&rsquo;Agenzia. Chi rientra nel perimetro e non si è
          ancora registrato dovrebbe rimediare con priorità: la registrazione
          è il primo dato che l&rsquo;autorità verifica.
        </P>
      </GuideSection>

      <GuideSection id="obblighi" title="Gli obblighi: misure di gestione del rischio">
        <P>
          Il cuore del decreto sono misure tecniche e organizzative adeguate
          e proporzionate per gestire i rischi per le reti e i sistemi
          informativi. La direttiva elenca espressamente, tra l&rsquo;altro:
        </P>
        <UL>
          <LI>analisi dei rischi e politiche di sicurezza informatica;</LI>
          <LI>
            <strong>gestione degli incidenti</strong>: saper rilevare,
            analizzare, contenere e ripristinare, e poterlo dimostrare;
          </LI>
          <LI>continuità operativa, backup e gestione delle crisi;</LI>
          <LI>sicurezza della catena di approvvigionamento (supply chain);</LI>
          <LI>sicurezza nello sviluppo, nell&rsquo;acquisto e nella manutenzione dei sistemi;</LI>
          <LI>procedure per misurare l&rsquo;efficacia delle misure;</LI>
          <LI>igiene informatica e formazione sulla sicurezza;</LI>
          <LI>crittografia e, dove appropriato, cifratura;</LI>
          <LI>controllo degli accessi, sicurezza del personale e gestione degli asset;</LI>
          <LI>autenticazione a più fattori e comunicazioni protette.</LI>
        </UL>
        <P>
          Elemento centrale è la{' '}
          <strong>responsabilità degli organi di amministrazione</strong>,
          conforme a NIS2: il vertice approva le misure, ne sorveglia
          l&rsquo;attuazione e segue una formazione in materia di
          cybersicurezza. In caso di violazioni gravi la responsabilità può
          diventare personale.
        </P>
        <P>
          Con la scadenza del 18 aprile 2026 alle spalle, questi obblighi di
          base non sono più un traguardo da raggiungere: sono lo standard
          rispetto al quale l&rsquo;ACN misura le organizzazioni durante le
          verifiche.
        </P>
      </GuideSection>

      <GuideSection id="obbligo-di-notifica" title="L&rsquo;obbligo di notifica: 24 ore, 72 ore, un mese">
        <P>
          Gli incidenti significativi vanno notificati attraverso il CSIRT
          Italia, coordinato dall&rsquo;ACN, in tre fasi conformi alla
          direttiva:
        </P>
        <OL>
          <LI>
            <strong>Entro 24 ore</strong>: un pre-allarme, con una prima
            valutazione se l&rsquo;incidente sia riconducibile ad atti
            illegittimi o malevoli e se possa avere un impatto
            transfrontaliero.
          </LI>
          <LI>
            <strong>Entro 72 ore</strong>: una notifica completa con una
            prima valutazione di gravità e impatto e, ove disponibili, gli
            indicatori di compromissione (IOC).
          </LI>
          <LI>
            <strong>Entro un mese</strong>: una relazione finale con la
            descrizione dettagliata dell&rsquo;incidente, la causa probabile,
            le misure adottate e l&rsquo;eventuale impatto transfrontaliero.
          </LI>
        </OL>
        <P>
          Questi termini sono stretti. Chi inizia a raccogliere i log solo
          dopo un incidente non li rispetta: dire qualcosa di fondato entro
          24 ore su un possibile atto malevolo presuppone che i log rilevanti
          esistano già, siano ricercabili, e che il rilevamento abbia
          effettivamente notato l&rsquo;incidente.
        </P>
      </GuideSection>

      <GuideSection id="vigilanza-e-sanzioni" title="Vigilanza, sanzioni e responsabilità">
        <P>
          L&rsquo;autorità competente è l&rsquo;{' '}
          <strong>Agenzia per la Cybersicurezza Nazionale (ACN)</strong>:
          esercita la vigilanza, gestisce il registro delle entità e coordina
          la risposta agli incidenti. Nel 2026 l&rsquo;Agenzia è passata alla
          fase di vigilanza attiva ed è competente a condurre ispezioni e
          audit presso i soggetti registrati.
        </P>
        <P>Le sanzioni sono di livello GDPR, conformi a NIS2:</P>
        <UL>
          <LI>
            soggetti essenziali: fino a{' '}
            <strong>10 milioni di euro o il 2%</strong> del fatturato annuo
            mondiale;
          </LI>
          <LI>
            per i soggetti importanti valgono massimali più bassi, sempre
            secondo lo schema della direttiva.
          </LI>
        </UL>
        <P>
          Oltre alle sanzioni pecuniarie, l&rsquo;autorità può impartire
          prescrizioni vincolanti, e gli organi di amministrazione rispondono
          delle violazioni degli obblighi di approvazione e sorveglianza.
        </P>
      </GuideSection>

      <GuideSection id="logging-e-monitoraggio" title="Cosa significa in concreto per logging e monitoraggio">
        <P>
          Logging e monitoraggio compaiono in quasi ogni obbligo: senza i log
          giusti non puoi rilevare un incidente, non puoi motivare un
          pre-allarme entro 24 ore e non puoi scrivere una relazione finale.
          In pratica:
        </P>
        <H3>Centralizza i log</H3>
        <P>
          I log di server, apparati di rete, identity provider, servizi cloud
          e applicazioni vanno in un unico sistema ricercabile. Ricostruire
          un incidente attraverso dieci sistemi separati non riesce entro 72
          ore.
        </P>
        <H3>Conservali abbastanza a lungo, in modo dimostrabile</H3>
        <P>
          La direttiva non fissa un termine di conservazione: il criterio è
          basato sul rischio, cioè poter indagare e notificare gli incidenti.
          Nella pratica sono comuni 6-18 mesi, con un livello &ldquo;hot&rdquo;
          ricercabile e un archivio più economico. Documenta la scelta e la
          motivazione: è esattamente ciò che un&rsquo;autorità di vigilanza
          chiede durante un audit.
        </P>
        <H3>Monitora in tempo reale, non a posteriori</H3>
        <P>
          I termini di notifica presuppongono che gli incidenti si vedano
          mentre accadono. Servono regole di rilevamento sui log (tentativi
          di accesso falliti, comportamenti anomali, connessioni di rete
          sospette) e un sistema di allerta che raggiunga una persona in
          tempo, senza annegare nei falsi allarmi.
        </P>
        <H3>Mantieni un audit trail</H3>
        <P>
          Chi ha fatto cosa e quando, anche all&rsquo;interno della
          piattaforma di logging stessa? Un audit trail a prova di
          manomissione sostiene sia le notifiche di incidente sia la
          rendicontazione verso l&rsquo;autorità.
        </P>
        <P>
          Un punto di onestà: nessun prodotto rende conforme da solo, NIS2
          resta un percorso organizzativo. LogPulse copre il lato logging,
          rilevamento e monitoraggio: gestione centralizzata dei log con
          retention configurabile, oltre 50 rilevamenti integrati con mapping
          MITRE ATT&amp;CK tramite il{' '}
          <GuideLink href="/siem">SIEM di LogPulse</GuideLink>, allerta
          basata sul rischio che produce una manciata di incidenti reali
          invece di migliaia di segnalazioni, e report di compliance che
          collegano rilevamenti e dati agli obblighi NIS2. Tutti i dati
          restano nell&rsquo;UE (GCP Amsterdam), il che semplifica
          notevolmente la rendicontazione verso l&rsquo;autorità e gli
          accordi sul trattamento dei dati.
        </P>
      </GuideSection>

      <GuideSection id="piano-d-azione" title="Piano d&rsquo;azione: da dove iniziare">
        <OL>
          <LI>
            <strong>Verifica il perimetro</strong>: stabilisci se la tua
            organizzazione rientra tra i soggetti essenziali o importanti e
            controlla lo stato della registrazione sul portale ACN.
          </LI>
          <LI>
            <strong>Coinvolgi il vertice</strong>: gli organi di
            amministrazione approvano le misure e ne rispondono.
          </LI>
          <LI>
            <strong>Esegui un&rsquo;analisi dei rischi</strong> e mappa i
            sistemi e i fornitori critici per i tuoi servizi.
          </LI>
          <LI>
            <strong>Centralizza il logging e attiva il rilevamento</strong>:
            è il fondamento sia delle misure di gestione del rischio sia
            dell&rsquo;obbligo di notifica.
          </LI>
          <LI>
            <strong>Esercita il processo di notifica</strong>: chi informa il
            CSIRT Italia entro 24 ore, e con quali informazioni?
          </LI>
          <LI>
            <strong>Documenta tutto</strong>: misure, scelte, termini di
            conservazione, esercitazioni. Una compliance che non puoi
            dimostrare, per un&rsquo;autorità di vigilanza non esiste.
          </LI>
        </OL>
        <P>
          Vuoi sapere come LogPulse copre il lato logging, rilevamento e
          reportistica? Vedi{' '}
          <GuideLink href="/compliance/nis2">Compliance NIS2 con LogPulse</GuideLink>{' '}
          e i <GuideLink href="/pricing">piani e prezzi</GuideLink>, oppure
          inizia gratis e configura i primi rilevamenti oggi stesso.
        </P>
      </GuideSection>
    </>
  );
}
